import { FC, useMemo } from 'react';

import { ViewNode, WarehouseEntityType } from '@/__generated__/graphql';
import { ColumnTable, Entry } from '@/components/ColumnTable';
import { TagLinks } from '@/components/ResourcePanel/ResourcePanels/TagLinks';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useWarehouseEntity } from '@/hooks/discovery/catalog/entities';
import { formatAbsoluteLocalDate } from '@/util/date';
import { LocationDetails } from './LocationDetails';
import { AssetDetailsParams, PanelDetails } from './types';

const createViewRows = (view: ViewNode): Entry[] => {
  return [
    {
      key: 'tags',
      data: <TagLinks tags={view.tags} />,
    },
    {
      key: 'Relation',
      data: (
        <div>{view.qualifiedName && <div>{view.qualifiedName.toLowerCase()}</div>}</div>
      ),
    },
    {
      key: 'Location',
      data: (
        <LocationDetails
          fqn={view.qualifiedName}
          entityType={WarehouseEntityType.View}
        />
      ),
    },
    {
      key: 'Last synced',
      data: view.lastSyncedAt ? (
        formatAbsoluteLocalDate(new Date(view.lastSyncedAt))
      ) : (
        <div>Unknown</div>
      ),
    },
  ];
};

export const useViewDetailsPanel = ({ assetId }: AssetDetailsParams) => {
  const { accountId } = useDbtCloudAccount();

  const viewQuery = useWarehouseEntity({
    accountId,
    filter: {
      urn: assetId,
      parent_urn: '',
      type: null,
    },
  });

  return useMemo(() => {
    const view = viewQuery.data as ViewNode | undefined;
    if (!view)
      return { ...viewQuery, rows: null, description: null } satisfies PanelDetails;

    const rows = createViewRows(view);

    return {
      ...viewQuery,
      rows,
      description: view?.description,
    };
  }, [viewQuery]);
};

type ViewDetailsProps = {
  assetId: string;
  compact?: boolean;
};

export const ViewDetails: FC<ViewDetailsProps> = ({ assetId, compact }) => {
  const viewDetails = useViewDetailsPanel({ assetId });
  return (
    <div>
      <ColumnTable
        tableEntries={viewDetails.rows}
        compact={compact}
        isLoading={viewDetails.fetching}
      />
    </div>
  );
};
